import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useImageLightbox } from './ImageLightboxContext';
import { useT } from '../I18nContext';

/**
 * Centred image overlay. Click outside, click the X, or press
 * Escape to close.
 *
 * Renders nothing until something opens it. Mounted near the
 * dashboard root so it overlays the entire UI.
 */
export function ImageLightbox() {
  const { src, close } = useImageLightbox();
  const t = useT();

  // Esc to close.
  useEffect(() => {
    if (!src) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, close]);

  if (!src) return null;

  return (
    <div
      className="image-lightbox"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={t('lightbox.title')}
    >
      <button
        type="button"
        className="image-lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
        aria-label={t('lightbox.close')}
      >
        <X size={20} />
      </button>
      <img
        className="image-lightbox-img"
        src={src}
        alt=""
        // Block click-through so clicking the image itself doesn't
        // close the lightbox - only the dim background does.
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
